import { PRIMARY_ATTRS, BASE_ATTRS } from '../combat/CombatStats.js';

/**
 * CharacterProgression - XP, leveling, and primary attribute system.
 * Max level 99, 3 attribute points per level.
 * Attributes: STR, DEX, INT, CON, WIS, END.
 */
export default class CharacterProgression {
  static MAX_LEVEL = 99;
  static POINTS_PER_LEVEL = 3;

  constructor() {
    this.level = 1;
    this.xp = 0;
    this.totalXp = 0;
    this.attributes = { ...BASE_ATTRS };
    this.unspentPoints = 0;
    /** @type {Function|null} callback(newLevel, baseStats) */
    this.onLevelUp = null;
    /** @type {Function|null} callback() for save trigger */
    this.onChange = null;
  }

  /**
   * XP required to go FROM a given level to the next.
   * Curve: 50 * level * (1 + level * 0.05)
   * L1→52, L10→525, L20→1500, L50→6375, L99→24997
   */
  static xpForLevel(level) {
    return Math.floor(50 * level * (1 + level * 0.05));
  }

  /** XP needed to reach next level from current XP. */
  get xpToNextLevel() {
    if (this.level >= CharacterProgression.MAX_LEVEL) return 0;
    return CharacterProgression.xpForLevel(this.level);
  }

  /** Fraction of progress toward next level [0, 1). */
  get xpProgress() {
    if (this.level >= CharacterProgression.MAX_LEVEL) return 1;
    return this.xp / this.xpToNextLevel;
  }

  /**
   * Award XP, handling multi-level-ups.
   * @returns {number} number of levels gained
   */
  addXp(amount) {
    if (this.level >= CharacterProgression.MAX_LEVEL || amount <= 0) return 0;
    this.xp += amount;
    this.totalXp += amount;
    let levelsGained = 0;

    while (this.level < CharacterProgression.MAX_LEVEL &&
           this.xp >= CharacterProgression.xpForLevel(this.level)) {
      this.xp -= CharacterProgression.xpForLevel(this.level);
      this.level++;
      this.unspentPoints += CharacterProgression.POINTS_PER_LEVEL;
      levelsGained++;
      if (this.onLevelUp) this.onLevelUp(this.level, this.getBaseStats());
    }
    if (this.level >= CharacterProgression.MAX_LEVEL) this.xp = 0;
    if (this.onChange) this.onChange();
    return levelsGained;
  }

  /**
   * Allocate one point to an attribute.
   * @param {string} attr - One of PRIMARY_ATTRS
   * @returns {boolean} success
   */
  allocatePoint(attr) {
    if (this.unspentPoints <= 0) return false;
    if (!PRIMARY_ATTRS.includes(attr)) return false;
    this.attributes[attr]++;
    this.unspentPoints--;
    if (this.onChange) this.onChange();
    return true;
  }

  /**
   * Reset all attributes to base, refund all spent points.
   * @param {number} goldCost - Gold to deduct (caller handles deduction)
   */
  redistributeStats() {
    const totalSpent = PRIMARY_ATTRS.reduce((sum, a) => sum + this.attributes[a], 0)
      - PRIMARY_ATTRS.reduce((sum, a) => sum + BASE_ATTRS[a], 0);
    for (const a of PRIMARY_ATTRS) {
      this.attributes[a] = BASE_ATTRS[a];
    }
    this.unspentPoints += totalSpent;
    if (this.onChange) this.onChange();
  }

  /** Gold cost for stat redistribution: 100 * level */
  get respecCost() {
    return 100 * this.level;
  }

  /**
   * Derive base stats from current attributes.
   * Same formulas as CombatStats.deriveFromAttributes.
   */
  getBaseStats() {
    const a = this.attributes;
    return {
      maxHealth: 100 + a.con * 5,
      maxMana: 50 + a.int * 4 + a.wis * 2,
      maxStamina: 100 + a.end * 3,
      attackPower: 10,
      defense: 0,
      healthRegen: 0.2 + a.con * 0.04,
      manaRegen: 0.8 + a.wis * 0.35,
      staminaRegen: 4.0 + a.end * 0.4 + a.dex * 0.15,
      meleeDamageBonus: Math.floor(a.str * 0.5),
      rangedDamageBonus: Math.floor(a.dex * 0.55),
      spellDamageBonus: Math.floor(a.int * 0.8 + a.wis * 0.25),
      attackSpeed: 1.0 + a.dex * 0.015,
      maxCarryWeight: 100 + a.str * 2,
    };
  }

  /**
   * Apply base stats from current attributes onto a CombatStats instance.
   * Accounts for equipment attribute bonuses on the combatStats.
   */
  applyTo(combatStats) {
    // Compute effective attributes: base + equipment bonuses
    const a = {};
    for (const attr of PRIMARY_ATTRS) {
      const bonusKey = 'bonus' + attr.charAt(0).toUpperCase() + attr.slice(1);
      a[attr] = this.attributes[attr] + (combatStats[bonusKey] || 0);
    }

    const prevMaxHealth = combatStats.maxHealth;
    const prevMaxMana = combatStats.maxMana;
    const prevMaxStamina = combatStats.maxStamina;

    combatStats.maxHealth = 100 + a.con * 5;
    combatStats.maxMana = 50 + a.int * 4 + a.wis * 2;
    combatStats.maxStamina = 100 + a.end * 3;
    combatStats.attackPower = 10;
    combatStats.defense = 0;
    combatStats.healthRegen = 0.2 + a.con * 0.04;
    combatStats.manaRegen = 0.8 + a.wis * 0.35;
    combatStats.staminaRegen = 4.0 + a.end * 0.4 + a.dex * 0.15;
    combatStats.meleeDamageBonus = Math.floor(a.str * 0.5);
    combatStats.rangedDamageBonus = Math.floor(a.dex * 0.55);
    combatStats.spellDamageBonus = Math.floor(a.int * 0.8 + a.wis * 0.25);
    combatStats.attackSpeed = 1.0 + a.dex * 0.015;

    // Clamp current values to new maximums (but scale up proportionally if max increased)
    if (combatStats.health > combatStats.maxHealth) combatStats.health = combatStats.maxHealth;
    if (prevMaxHealth > 0 && combatStats.maxHealth > prevMaxHealth) {
      combatStats.health += (combatStats.maxHealth - prevMaxHealth);
      if (combatStats.health > combatStats.maxHealth) combatStats.health = combatStats.maxHealth;
    }
    if (combatStats.mana > combatStats.maxMana) combatStats.mana = combatStats.maxMana;
    if (prevMaxMana > 0 && combatStats.maxMana > prevMaxMana) {
      combatStats.mana += (combatStats.maxMana - prevMaxMana);
      if (combatStats.mana > combatStats.maxMana) combatStats.mana = combatStats.maxMana;
    }
    if (combatStats.stamina > combatStats.maxStamina) combatStats.stamina = combatStats.maxStamina;
    if (prevMaxStamina > 0 && combatStats.maxStamina > prevMaxStamina) {
      combatStats.stamina += (combatStats.maxStamina - prevMaxStamina);
      if (combatStats.stamina > combatStats.maxStamina) combatStats.stamina = combatStats.maxStamina;
    }
  }

  /** Compute XP value for killing an enemy, scaled by level difference. */
  static getXpFromEnemy(combatStats, playerLevel = 1) {
    if (!combatStats) return 0;
    let base = Math.round(combatStats.maxHealth * 0.5);
    if (combatStats.isBoss) base *= 3;
    // Level difference scaling
    const npcLevel = combatStats.level || 1;
    const diff = npcLevel - playerLevel;
    const mul = diff >= 0 ? 1 + diff * 0.10 : Math.max(0.1, 1 + diff * 0.15);
    return Math.max(5, Math.round(base * mul));
  }

  toJSON() {
    return {
      level: this.level,
      xp: this.xp,
      totalXp: this.totalXp,
      attributes: { ...this.attributes },
      unspentPoints: this.unspentPoints,
    };
  }

  fromJSON(data) {
    if (!data) return;
    this.level = data.level || 1;
    this.xp = data.xp || 0;
    this.totalXp = data.totalXp || 0;
    if (data.attributes) {
      for (const a of PRIMARY_ATTRS) {
        this.attributes[a] = data.attributes[a] ?? BASE_ATTRS[a];
      }
    }
    this.unspentPoints = data.unspentPoints || 0;
  }
}
